import { createContext, useContext, useMemo } from 'react';
import standardCohortKeys from 'js/utils/cohort-keys';
import { getPageCapabilities } from 'js/engines/CapabilitiesAssessment/utils';
import { buildCapabilitySetsFromCache } from 'js/ydoc/docs/capabilities-assessment/capability-utils';

function getPageData(page, _assessment, capabilitySets) {
  switch (page.type) {
    case 'standard-questions':
      return { elements: page.elements ?? [] };
    case 'capability-questions':
      return {
        pageCapabilities: getPageCapabilities(
          capabilitySets[page.capabilitySetId],
          page.selectedCapabilities
        ),
        capabilityElements: Object.fromEntries(
          page.capabilityElements.map((ce) => [ce.key, ce])
        ),
      };
    default:
      return {};
  }
}

export const AssessmentReviewContext = createContext({
  questionMap: {},
  questionIdMap: {},
  cohorts: [],
  participantCohortMap: {},
  pageData: {},
  capabilitySets: {},
});

export function AssessmentReviewContextProvider({ children, assessment }) {
  const { questionMap, questionIdMap } = useMemo(() => {
    return assessment.question_sets.reduce(
      (qsAcc, qs) =>
        qs.questions.reduce((qAcc, q) => {
          qAcc.questionMap[q.key] = q;
          qAcc.questionIdMap[q.id] = q;
          return qAcc;
        }, qsAcc),
      { questionMap: {}, questionIdMap: {} }
    );
  }, [assessment]);

  const capabilitySets = useMemo(() => {
    if (assessment?.document) {
      return buildCapabilitySetsFromCache(
        assessment.document.capabilitySetCache,
        assessment.document.capabilityCache
      );
    }
    return {};
  }, [assessment]);

  const { pageData, capabilityElements } = useMemo(() => {
    return assessment.document.pages.reduce(
      (pAcc, p) => {
        const pd = getPageData(p, assessment, capabilitySets);
        pAcc.pageData[p.guid] = pd;

        Object.values(pd.capabilityElements ?? {}).forEach((ce) => {
          if (!pAcc.capabilityElements[ce.key]) {
            pAcc.capabilityElements[ce.key] = {
              ...ce,
              paletteIndex: Object.values(pAcc.capabilityElements).length + 2,
            };
          }
        });
        return pAcc;
      },
      { pageData: {}, capabilityElements: {} }
    );
  }, [assessment, capabilitySets]);

  const { cohorts, participantCohortMap } = useMemo(() => {
    const { cohortMap, participantMap: participantCohortMap } = (
      assessment?.participants ?? []
    ).reduce(
      (acc, p) => {
        const cohort =
          p.cohort === null
            ? { value: standardCohortKeys.NULL, text: '(None)' }
            : { value: p.cohort, text: p.cohort };
        acc.cohortMap[cohort.value] = cohort;
        acc.participantMap[p.id] = cohort.value;
        return acc;
      },
      { cohortMap: {}, participantMap: {} }
    );

    return {
      cohorts: Object.values(cohortMap),
      participantCohortMap,
    };
  }, [assessment]);

  const context = useMemo(() => {
    return {
      questionMap,
      questionIdMap,
      cohorts,
      participantCohortMap,
      pageData,
      capabilitySets,
      capabilityElementMap: capabilityElements,
    };
  }, [
    questionMap,
    questionIdMap,
    cohorts,
    participantCohortMap,
    pageData,
    capabilitySets,
    capabilityElements,
  ]);

  return (
    <AssessmentReviewContext.Provider value={context}>
      {children}
    </AssessmentReviewContext.Provider>
  );
}

export function useAssessmentReviewContext() {
  return useContext(AssessmentReviewContext);
}
